import { fetchOsmOverpass } from 'api/otherApi';
import { getGeodata } from 'api/geodataApi';
import { getGrid } from 'api/gridApi';
import { lv95ToWgs84ForOneCoordinate } from 'views/add-grid/map/coordinateTransforms';
import { NOISE_ROADS_VALIDATION_RADIUS_METERS } from 'constants/simulation';

interface GridParamsLike {
  manualCenter?: {
    x?: number | string;
    y?: number | string;
  } | null;
  [key: string]: unknown;
}

interface NoiseSubSimulation {
  simType?: string;
  params?: {
    filters?: (string | null | undefined)[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface SimulationLike {
  subSimulations?: NoiseSubSimulation[];
  [key: string]: unknown;
}

const getManualCenterFromGridParams = (gridParams: GridParamsLike = {}) => {
  const x = Number(gridParams?.manualCenter?.x);
  const y = Number(gridParams?.manualCenter?.y);
  const hasCenter = Number.isFinite(x) && Number.isFinite(y) && (x !== 0 || y !== 0);
  if (!hasCenter) {
    return null;
  }

  const { lat, lng } = lv95ToWgs84ForOneCoordinate(x, y);
  return { lat, lng };
};

const unwrapSingleItem = <T>(payload: T | T[]): T => (Array.isArray(payload) ? payload[0] : payload);

const findNoiseSubSimulations = (simulation: SimulationLike | null | undefined): NoiseSubSimulation[] =>
  (simulation?.subSimulations || []).filter((subSim) => subSim?.simType === 'NOISE');

interface ValidateNoiseRoadsArgs {
  simulation: SimulationLike | null | undefined;
  geodataId: string | number | null | undefined;
}

export const validateNoiseRoadsForSimulationSubmit = async ({ simulation, geodataId }: ValidateNoiseRoadsArgs) => {
  const noiseSubSimulations = findNoiseSubSimulations(simulation);
  if (noiseSubSimulations.length === 0) {
    return { shouldProceed: true, type: 'skip' };
  }

  if (!geodataId) {
    return { shouldProceed: true, type: 'warning_unreachable' };
  }

  let center = null;

  try {
    const geodataItem = unwrapSingleItem(await getGeodata(String(geodataId)));
    const gridId = geodataItem?.gridId;

    if (!gridId) {
      return { shouldProceed: true, type: 'warning_unreachable' };
    }

    const gridItem = unwrapSingleItem(await getGrid(String(gridId)));
    center = getManualCenterFromGridParams(gridItem?.params || {});
  } catch (error) {
    console.warn('Unable to fetch geodata/grid for OSM noise validation', error);
    return { shouldProceed: true, type: 'warning_unreachable' };
  }

  if (!center) {
    return { shouldProceed: true, type: 'warning_unreachable' };
  }

  try {
    for (const subSimulation of noiseSubSimulations) {
      const filters = (subSimulation?.params?.filters || []).filter(Boolean);
      const highwayFilter = filters.length === 0 ? '["highway"]' : `["highway"~"^(${filters.join('|')})$"]`;
      const query = `[out:json][timeout:20];
way${highwayFilter}(around:${NOISE_ROADS_VALIDATION_RADIUS_METERS},${center.lat},${center.lng});
out ids 1;`;

      const response = await fetchOsmOverpass(query);

      if (!response.ok) {
        throw new Error(`OSM request failed with status ${response.status}`);
      }

      const payload = await response.json();
      const hasRoads = Array.isArray(payload?.elements) && payload.elements.length > 0;
      if (!hasRoads) {
        return { shouldProceed: false, type: 'error_no_roads' };
      }
    }

    return { shouldProceed: true, type: 'ok' };
  } catch (error) {
    console.warn('Unable to validate noise roads via OSM', error);
    return { shouldProceed: true, type: 'warning_unreachable' };
  }
};
